import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { teacherPriorityApi, type PriorityRequestListItem } from '@/services/api/teacherPriority'
import { teacherApi, type RequestLesson } from '@/services/api/teacher'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

interface PriorityCardTimelineTabProps {
  request: PriorityRequestListItem
}

interface TimelineEvent {
  type: 'created' | 'contacted' | 'accepted' | 'declined' | 'timed_out' | 'deadline'
      | 'prep_requested' | 'prep_delivered' | 'cancelled' | 'cancellation_requested'
      | 'waiting_pool'
      | 'recontacted'
      | 'director_gate' | 'director_returned' | 'director_decision'
      | 'prep_accepted' | 'prep_fully_accepted'
  label: string
  timestamp: string
  color: 'green' | 'red' | 'blue' | 'orange' | 'gray' | 'purple' | 'amber'
  isBold?: boolean
  details?: Array<{
    date: string
    time_slot_name: string
    subject_name: string
    klass_name: string
    room_name: string
  }>
}

const DOT_COLORS: Record<string, string> = {
  green: 'bg-green-500',
  red: 'bg-red-500',
  blue: 'bg-blue-500',
  orange: 'bg-orange-400',
  gray: 'bg-gray-400',
  purple: 'bg-purple-500',
  amber: 'bg-amber-400',
}

export function PriorityCardTimelineTab({ request }: PriorityCardTimelineTabProps) {
  const [showAll, setShowAll] = useState(false)
  const [groupByDay, setGroupByDay] = useState(request.request_flow_strategy === 'smart_batch')
  const [lessonDetails, setLessonDetails] = useState<RequestLesson[]>([])

  const { data: status, isLoading } = useQuery({
    queryKey: ['request-status', request.id],
    queryFn: () => teacherPriorityApi.getRequestStatus(request.id),
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  })

  const { data: prepRequests = [] } = useQuery({
    queryKey: ['prep-requests', request.id],
    queryFn: () => teacherPriorityApi.getPrepRequests(request.id),
  })

  // prepgruen D11: Akzeptanz-Verlauf — Preps liefern acceptances + Zaehler
  const { data: preps = [] } = useQuery({
    queryKey: ['preps', request.id],
    queryFn: () => teacherPriorityApi.getPreps(request.id),
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  })

  const { data: allCancellations = [] } = useQuery({
    queryKey: ['cancellation-requests'],
    queryFn: () => teacherPriorityApi.getCancellationRequests(),
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  })

  useEffect(() => {
    const hasWaitingPool = status?.coverage_blocks?.some(block =>
      block.entries.some(entry => entry.status === 'waiting')
    )
    
    if (hasWaitingPool) {
      teacherApi.getRequestLessons(request.id)
        .then(data => setLessonDetails(sortLessonsByDateTime(data.lessons)))
        .catch(() => setLessonDetails([]))
    }
  }, [request.id, status])

  const events: TimelineEvent[] = useMemo(() => {
    const result: TimelineEvent[] = []

    result.push({
      type: 'created',
      label: 'Anfrage erstellt',
      timestamp: request.created_at,
      color: 'blue',
    })

      // B6/R5: Director-Gate-Zustände als eigene Timeline-Ereignisse (sonst leere Timeline)
      if (request.director_enabled === true && request.status === 'director_pending') {
        result.push({
          type: 'director_gate',
          label: 'Pausiert — wartet auf Direktor-Freigabe',
          timestamp: request.created_at || request.cancelled_at || '',
          color: 'purple',
        })
      } else if (request.director_enabled === true && request.status === 'director_returned') {
        result.push({
          type: 'director_returned',
          label: 'Fall angepasst — Kaskade kann erneut ausgelöst werden',
          timestamp: request.cancelled_at || request.created_at || '',
          color: 'amber',
        })
      }
      if (
        request.director_enabled === true
        && (
          request.director_decision_type === 'approved'
          || request.director_decision_type === 'rejected'
        )
      ) {
        const payer = request.director_payment_type === 'school'
          ? ' · Schule'
          : request.director_payment_type === 'private'
            ? ' · Privat'
            : ''
        result.push({
          type: 'director_decision',
          label: request.director_decision_type === 'approved'
            ? `Direktor: Genehmigt${payer}`
            : 'Direktor: Abgelehnt',
          timestamp: request.created_at || '',
          color: request.director_decision_type === 'approved' ? 'green' : 'red',
        })
      }

      if (status?.coverage_blocks) {
        for (const block of status.coverage_blocks) {
          for (const entry of block.entries) {
            const teacherName = entry.teacher_name || entry.teacher_short_code || 'Lehrer'
            const code = entry.teacher_short_code || 'Lehrer'
            const dayPrefix = groupByDay && block.date
              ? `${new Date(block.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })} · `
              : ''
            if (entry.contacted_at) {
              result.push({
                type: 'contacted',
                label: `${dayPrefix}${teacherName} (${code}) kontaktiert`,
                timestamp: entry.contacted_at,
                color: 'orange',
              })
            }
            // prionew-v2: Wizard-Recontact — zweite Anfragerunde für offene
            // Lektionen sichtbar machen (kontaktiert_at wird beim Recontact
            // aktualisiert; status='recontacted' markiert die laufende Runde).
            if (entry.status === 'recontacted') {
              result.push({
                type: 'recontacted',
                label: `${teacherName} (${code}) erneut angefragt (offene Lektionen)`,
                timestamp: entry.contacted_at || entry.responded_at || '',
                color: 'orange',
              })
            }
            // Antwortfrist (Deadline) für aktuell kontaktierte Lehrer anzeigen —
            // clearly als Frist (Zukunft), NICHT als passiertes Timeout-Ereignis.
            // Nur für noch zukünftige Fristen (abgelaufene Frist + Status contacted
            // bedeutet: Timeout-Verarbeitung steht noch aus → kein verwirrendes Datum).
            if ((entry.status === 'contacted' || entry.status === 'recontacted') && entry.timeout_at) {
              const deadline = new Date(entry.timeout_at)
              if (!isNaN(deadline.getTime()) && deadline > new Date()) {
                result.push({
                  type: 'deadline',
                  label: `Antwortfrist ${teacherName} (${code})`,
                  timestamp: entry.timeout_at,
                  color: 'amber',
                })
              }
            }
            if (entry.responded_at) {
              if (entry.status === 'accepted') {
                result.push({
                  type: 'accepted',
                  label: `${teacherName} (${code}) hat angenommen`,
                  timestamp: entry.responded_at,
                  color: 'green',
                })
              } else if (entry.status === 'waiting') {
                const lessonCount = entry.provisional_lesson_ids?.length || 0
                const totalLessons = request.lesson_count || 0
                
                const waitingLessons = lessonDetails
                  .filter(l => entry.provisional_lesson_ids?.includes(l.id))
                  .map(l => ({
                    date: l.date || '',
                    time_slot_name: l.time_slot_name || '',
                    subject_name: l.subject_name || '',
                    klass_name: l.klass_name || '',
                    room_name: l.room_name || '',
                  }))
                
                result.push({
                  type: 'waiting_pool',
                  label: `${teacherName} (${code}) — ${lessonCount} von ${totalLessons} Lektionen im Wartepool`,
                  timestamp: entry.responded_at,
                  color: 'blue',
                  details: waitingLessons,
                })
              } else if (entry.status === 'declined') {
                const rejectionComment = entry.rejection_comment || null
                let declineLabel = `${teacherName} (${code}) hat abgelehnt`
                if (rejectionComment) {
                  declineLabel += `: "${rejectionComment}"`
                }
                result.push({
                  type: 'declined',
                  label: declineLabel,
                  timestamp: entry.responded_at,
                  color: 'red',
                })
              } else if (entry.status === 'recontacted_declined') {
                // prionew-v2: Absage auf die zweite Anfragerunde.
                result.push({
                  type: 'declined',
                  label: `${teacherName} (${code}) hat die erneute Anfrage abgelehnt`,
                  timestamp: entry.responded_at,
                  color: 'red',
                })
              }
            }
            // Kontaktfenster abgelaufen (debug-fix-v2): echter Verarbeitungszeitpunkt
            // (responded_at), nicht die Frist. Bedeutet NUR: die Kaskade rückt zur
            // nächsten Lehrperson vor — die Person kann weiterhin antworten.
            if (entry.status === 'timed_out' && entry.responded_at) {
              const respondedDate = new Date(entry.responded_at)
              // Guard: niemals ein zukünftiges Datum als Fenster-Ereignis anzeigen.
              if (!isNaN(respondedDate.getTime()) && respondedDate <= new Date()) {
                result.push({
                  type: 'timed_out',
                  label: `${teacherName} (${code}) — Antwortfenster abgelaufen (nächste Lehrperson angefragt, Annahme weiterhin möglich)`,
                  timestamp: entry.responded_at,
                  color: 'gray',
                })
              }
            }
            // prionew-v2: auch beim zweiten Timeout (Recontact-Fenster) — der
            // Lehrer bleibt für die Nothilfe (Stage 3) ansprechbar.
            if (entry.status === 'recontacted_timed_out' && entry.responded_at) {
              const respondedDate = new Date(entry.responded_at)
              if (!isNaN(respondedDate.getTime()) && respondedDate <= new Date()) {
                result.push({
                  type: 'timed_out',
                  label: `${teacherName} (${code}) — Antwortfenster der erneuten Anfrage abgelaufen`,
                  timestamp: entry.responded_at,
                  color: 'gray',
                })
              }
            }
          }
        }
      }

    for (const pr of prepRequests) {
      result.push({
        type: 'prep_requested',
        label: `Prep angefragt von ${pr.requested_by_teacher_name}`,
        timestamp: pr.created_at,
        color: 'purple',
      })
      if (pr.status === 'fulfilled' && pr.resolved_at) {
        result.push({
          type: 'prep_delivered',
          // prepgruen A1: Auto-Fulfill (resolved_by=NULL) unterscheidet sich im Label
          label: pr.resolved_by_teacher_id ? 'Prep geliefert' : 'Prep geliefert (alle Vertreter akzeptiert)',
          timestamp: pr.resolved_at,
          color: 'purple',
        })
      }
    }

    // prepgruen D11: ein Eintrag pro Akzeptierung + Extra-Eintrag bei 100 %
    for (const prep of preps) {
      for (const acc of prep.acceptances ?? []) {
        result.push({
          type: 'prep_accepted',
          label: `${acc.teacher_name} hat Prep «${prep.title}» akzeptiert`,
          timestamp: acc.accepted_at,
          color: 'green',
        })
      }
      if (prep.fully_accepted && (prep.acceptance_count ?? 0) > 0) {
        const lastAcceptedAt = (prep.acceptances ?? [])
          .map(a => a.accepted_at)
          .filter(Boolean)
          .sort()
          .pop()
        if (lastAcceptedAt) {
          result.push({
            type: 'prep_fully_accepted',
            label: `Prep «${prep.title}» vollständig akzeptiert (${prep.acceptance_count}/${prep.total_affected_teachers})`,
            timestamp: lastAcceptedAt,
            color: 'green',
            isBold: true,
          })
        }
      }
    }

    const relevantCancellations = allCancellations.filter(
      (c: { substitution_request_id: string }) => c.substitution_request_id === request.id
    )
    for (const c of relevantCancellations) {
      result.push({
        type: 'cancellation_requested',
        label: c.reason ? `Storno angefragt: ${c.reason}` : 'Storno angefragt',
        timestamp: c.created_at,
        color: 'amber',
      })
    }

    if (request.status === 'cancelled') {
      result.push({
        type: 'cancelled',
        label: 'Anfrage storniert',
        timestamp: request.cancelled_at || request.created_at,
        color: 'gray',
      })
    }

    result.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

    return result
  }, [request, status, prepRequests, allCancellations, lessonDetails, preps, groupByDay])

  const visibleEvents = showAll ? events : events.slice(0, 8)

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="flex gap-3">
          <div className="w-3 h-3 bg-gray-200 dark:bg-gray-700 rounded-full shrink-0 mt-0.5" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
        </div>
        <div className="flex gap-3">
          <div className="w-3 h-3 bg-gray-200 dark:bg-gray-700 rounded-full shrink-0 mt-0.5" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        </div>
        <div className="flex gap-3">
          <div className="w-3 h-3 bg-gray-200 dark:bg-gray-700 rounded-full shrink-0 mt-0.5" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-0">
      {(request.request_flow_strategy === 'smart_batch' || request.request_flow === 'prioritized') && (
        <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-2">
          <input
            type="checkbox"
            checked={groupByDay}
            onChange={e => setGroupByDay(e.target.checked)}
          />
          Nach Tagen gruppieren
        </label>
      )}
      {visibleEvents.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
          Noch keine Ereignisse
        </p>
      ) : (
        visibleEvents.map((evt, i) => {
          const isDeadline = evt.type === 'deadline'
          return (
          <div key={`${evt.type}-${evt.timestamp}-${i}`} className="flex gap-3">
            <div className="flex flex-col items-center">
              {isDeadline ? (
                <div
                  className={`w-3 h-3 rounded-full border-2 shrink-0 mt-0.5 ${DOT_COLORS[evt.color] || 'bg-amber-50'} border-amber-400`}
                />
              ) : (
                <div
                  className={`w-3 h-3 rounded-full ${DOT_COLORS[evt.color] || 'bg-gray-400'} shrink-0 mt-0.5`}
                />
              )}
              {i < visibleEvents.length - 1 && (
                <div className="w-0.5 flex-1 min-h-[1.5rem] bg-gray-200 dark:bg-gray-700" />
              )}
            </div>
            <div className="pb-3">
              <p className={`text-xs ${isDeadline ? 'italic text-amber-600 dark:text-amber-400' : evt.isBold ? 'font-bold text-green-700 dark:text-green-300' : 'font-medium text-gray-800 dark:text-gray-200'}`}>
                {evt.label}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {new Date(evt.timestamp).toLocaleString('de-DE', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
              {evt.type === 'waiting_pool' && evt.details && evt.details.length > 0 && (
                <div className="mt-2 space-y-1">
                  {evt.details.map((lesson, idx) => (
                    <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 rounded px-2 py-1">
                      <span className="font-medium">{lesson.date ? new Date(lesson.date).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' }) : ''}</span>
                      {' • '}{lesson.time_slot_name}
                      {' • '}{lesson.subject_name}
                      {' • '}{lesson.klass_name}
                      {' • Raum '}{lesson.room_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )
        })
      )}
      {events.length > 8 && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline ml-6"
        >
          +{events.length - 8} weitere anzeigen
        </button>
      )}
    </div>
  )
}
